/**
 * Command Registry — VS Code-style command system for workspace menus
 *
 * Focusable areas (explorer, editor grid, SQL pane, schema sidebar) register
 * their commands via useCommandScope(). The menu bar reads from the registry
 * via useCommandRegistry().getMenuCommands('edit'|'file').
 *
 * Scope hierarchy: deeper scopes shadow parent commands with the same id.
 *   workspace (Save, Undo, Redo)
 *     explorer (Copy/Cut/Paste/Delete files, Rename, AI Access)
 *     editor (wraps EditorEditHandle — compatibility layer)
 *       editor:db:grid (Copy cells, Delete rows, Insert, Duplicate)
 *       editor:db:sql (Run query)
 *
 * Focus tracking: useFocusScope() attaches a mousedown listener on a container
 * and sets the active scope when clicked. No centralized DOM inspection.
 *
 * KEYBOARD (#900): a provider given `keyboard` dispatches its commands' shortcuts from a `window` keydown listener —
 * and only then, because every open workspace keeps its own provider mounted behind `display:none`. A shortcut runs
 * only when ALL of these hold:
 *   - nothing else handled the keystroke (`defaultPrevented` — the explorer's own keys, Cmd+S, Ctrl+Z);
 *   - the user is not typing (`isTypingTarget`) and it isn't an AltGr chord;
 *   - the keystroke comes from INSIDE the region of the scope chain it resolves from: the focused element, or — when
 *     focus is on the page itself — the last thing pressed. A scope's region is its container (`useFocusScope`); a chain with no region is never dispatched (an office iframe, Univer: they own their keys).
 *     Without this, Backspace with the explorer's scope still active but the user elsewhere deleted files.
 *   - the command is enabled and not under a cover.
 *
 * COVERED scopes (#929/#930): something can cover an editor without unmounting
 * it — a version preview (`absolute inset-0` over the live editor), the conflict
 * view's "Saved version" panel (the live editor `display:none` behind a
 * read-only copy). The menu bar is NOT covered, and each editor registers its
 * own commands enabled on its own state, so without this its Edit commands
 * (Delete Rows…) acted on the hidden copy. `coverScope(id)` marks a scope
 * covered; every command found at or BELOW the highest covered scope in the
 * active chain resolves disabled — in the menus and for shortcuts alike — while
 * commands above it (the workspace's) are untouched. Whoever does the covering
 * marks it (see EditorCover.tsx); no editor has to guard its own commands, so
 * the next editor written gets this for free.
 */
import { createContext, useContext, useRef, useCallback, useEffect, type ReactNode, type ComponentType } from 'react'
import { isTypingTarget, isAltGrChord } from './keyboard'

// ─── Types ──────────────────────────────────────────────────────────────

export type MenuGroup = 'file' | 'edit' | 'view' | 'help'

export interface Shortcut {
  key: string       // e.g., 'c', 's', 'z', 'Backspace', 'F2'
  mod?: boolean      // Cmd (Mac) / Ctrl (Win)
  shift?: boolean
  alt?: boolean
}

export interface Command {
  id: string
  label: string
  icon?: ComponentType<{ size?: number }>
  shortcut?: Shortcut
  execute: () => void
  enabled: boolean
  menuGroup?: MenuGroup
  menuOrder?: number     // lower = earlier in menu
  danger?: boolean       // red text
  separator?: 'before' | 'after'
  hidden?: boolean       // in registry but not shown in menus
  submenu?: Command[]    // nested flyout (e.g., AI Access)
}

export type ScopeId = string

export interface CommandScope {
  scopeId: ScopeId
  parentScopeId?: ScopeId
  commands: Command[]
}

// ─── Context ────────────────────────────────────────────────────────────

export interface CommandRegistryValue {
  registerScope: (scope: CommandScope) => () => void
  setActiveScope: (scopeId: ScopeId) => void
  activeScopeId: ScopeId | null
  getMenuCommands: (group: MenuGroup) => Command[]
  /** Walk scope chain, find first matching enabled shortcut, execute it. Returns true if handled. */
  handleShortcut: (e: KeyboardEvent) => boolean
  /** Mark a scope COVERED (see the file header) until the returned release is called. Counted: two covers need two
   *  releases. The scope need not be registered — a missing parent (`'editor'` where no tab registers it) is still
   *  checked. */
  coverScope: (scopeId: ScopeId) => () => void
  /** Record a scope's DOM region (see the file header); the returned function forgets it. */
  setScopeRegion: (scopeId: ScopeId, el: HTMLElement) => () => void
  /** This provider dispatches shortcuts from the keyboard right now (#900). An editor's OWN listener for a key one of
   *  its commands declares stands aside while it does — the registry answers with all its rules — and answers only
   *  where no registry dispatches (the share view, mobile). */
  readonly dispatchesKeys: boolean
}

const CommandRegistryContext = createContext<CommandRegistryValue | null>(null)

export function useCommandRegistry(): CommandRegistryValue {
  const ctx = useContext(CommandRegistryContext)
  if (!ctx) throw new Error('useCommandRegistry must be used within CommandRegistryProvider')
  return ctx
}

/** Optional version — returns null if not inside a CommandRegistryProvider */
export function useCommandRegistryOptional(): CommandRegistryValue | null {
  return useContext(CommandRegistryContext)
}

// ─── Utilities ──────────────────────────────────────────────────────────

/** Controls that answer their own plain keys (#900): a shortcut never takes Backspace / F2 from them. */
const KEY_OWNING_CONTROLS = 'button, a[href], [role="button"], [role="menuitem"]'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

function matchesShortcut(shortcut: Shortcut, e: KeyboardEvent): boolean {
  const modKey = isMac ? e.metaKey : e.ctrlKey
  if ((shortcut.mod ?? false) !== modKey) return false
  if ((shortcut.shift ?? false) !== e.shiftKey) return false
  if ((shortcut.alt ?? false) !== e.altKey) return false
  // With Option held, macOS reports the character it composes, not the key (⇧⌥F is "Ï"), so an Alt+letter
  // shortcut matches the PHYSICAL key. Everywhere else `key` is the letter anyway.
  if (shortcut.alt && /^[a-z]$/i.test(shortcut.key) && e.code) return e.code === `Key${shortcut.key.toUpperCase()}`
  return e.key.toLowerCase() === shortcut.key.toLowerCase()
}

export function formatShortcut(s: Shortcut): string {
  const parts: string[] = []
  if (s.mod) parts.push(isMac ? '⌘' : 'Ctrl+')
  if (s.shift) parts.push(isMac ? '⇧' : 'Shift+')
  if (s.alt) parts.push(isMac ? '⌥' : 'Alt+')
  // Friendly key names
  const keyMap: Record<string, string> = {
    backspace: '⌫', delete: '⌫', enter: '↵', escape: 'Esc',
    arrowup: '↑', arrowdown: '↓', arrowleft: '←', arrowright: '→',
  }
  parts.push(keyMap[s.key.toLowerCase()] || s.key.toUpperCase())
  return parts.join('')
}

/**
 * The active chain, innermost first, each scope paired with whether it is UNDER A COVER: at or below the highest
 * covered scope id on the walk. The first missing id ends the walk (its own commands are unknown) but is still
 * checked for a cover — an editor's scope may name a parent nobody registered (`'editor'` in the single-file view).
 */
function getScopeChain(
  scopeId: ScopeId | null,
  scopes: Map<ScopeId, CommandScope>,
  covers: Map<ScopeId, number>,
): Array<{ scope: CommandScope; covered: boolean }> {
  const chain: CommandScope[] = []
  let lastCovered = -1
  let id = scopeId
  while (id) {
    if ((covers.get(id) ?? 0) > 0) lastCovered = chain.length
    const scope = scopes.get(id)
    if (!scope) break
    chain.push(scope)
    id = scope.parentScopeId ?? null
  }
  return chain.map((scope, i) => ({ scope, covered: i <= lastCovered }))
}

function resolveMenuCommands(chain: Array<{ scope: CommandScope; covered: boolean }>, group: MenuGroup): Command[] {
  const seen = new Set<string>()
  const result: Command[] = []
  for (const { scope, covered } of chain) {
    const cmds = scope.commands
    for (const cmd of cmds) {
      if (cmd.menuGroup === group && !seen.has(cmd.id)) {
        seen.add(cmd.id)
        // Still SHOWN under a cover (greyed): the menu keeps its shape, and the id stays claimed so a parent's
        // same-id command can't stand in for it.
        if (!cmd.hidden) result.push(covered && cmd.enabled ? { ...cmd, enabled: false } : cmd)
      }
    }
  }
  return result.sort((a, b) => (a.menuOrder ?? 999) - (b.menuOrder ?? 999))
}

// ─── Provider ───────────────────────────────────────────────────────────

export function CommandRegistryProvider({ children, keyboard = false }: {
  children: ReactNode
  /** Dispatch shortcuts from the keyboard (#900) — only while this provider's surface is ON SCREEN. Off by default. */
  keyboard?: boolean
}) {
  const scopesRef = useRef<Map<ScopeId, CommandScope>>(new Map())
  const activeScopeRef = useRef<ScopeId | null>(null)
  const coversRef = useRef<Map<ScopeId, number>>(new Map())
  const keyboardRef = useRef(keyboard)
  keyboardRef.current = keyboard
  const regionsRef = useRef<Map<ScopeId, HTMLElement>>(new Map())

  const registerScope = useCallback((scope: CommandScope): (() => void) => {
    scopesRef.current.set(scope.scopeId, scope)
    // Only bump version on new registration (not updates — avoids re-render loops).
    // Menu reads fresh data from the ref when it opens.
    // No state update needed — menu reads from ref when it opens
    return () => {
      scopesRef.current.delete(scope.scopeId)
      // Don't reset activeScopeRef here — the scope may be immediately
      // re-registered (e.g., useEffect cleanup+rerun on dep change).
      // getScopeChain handles missing scopes by breaking the chain.
    }
  }, [])

  /** Scopes made active, most recent last (deduped, bounded) — see {@link resolveActive}. */
  const activeHistoryRef = useRef<ScopeId[]>([])
  const setActiveScope = useCallback((scopeId: ScopeId) => {
    if (activeScopeRef.current !== scopeId) {
      activeScopeRef.current = scopeId
    }
    const h = activeHistoryRef.current.filter((id) => id !== scopeId)
    h.push(scopeId)
    activeHistoryRef.current = h.slice(-20)
  }, [])
  /**
   * The scope to resolve from: the active one, or — when it has gone (a version preview's grid, the conflict view's
   * read-only twin, closed under the user) — the most recent previously-active scope still registered. Without this
   * the chain was empty and the Edit menu fell back to actions that ignore covers.
   *
   * It cannot land on a HIDDEN tab's editor: every tab activation sets that tab's own scope ('editor', TabContent)
   * after anything the previous tab activated, so the newest registered entry always belongs to the tab on screen.
   */
  const resolveActive = (): ScopeId | null => {
    const active = activeScopeRef.current
    if (!active || scopesRef.current.has(active)) return active
    const h = activeHistoryRef.current
    for (let i = h.length - 1; i >= 0; i--) if (scopesRef.current.has(h[i]!)) return h[i]!
    return active
  }

  const coverScope = useCallback((scopeId: ScopeId): (() => void) => {
    const covers = coversRef.current
    covers.set(scopeId, (covers.get(scopeId) ?? 0) + 1)
    let released = false
    return () => {
      if (released) return
      released = true
      const n = (covers.get(scopeId) ?? 1) - 1
      if (n > 0) covers.set(scopeId, n)
      else covers.delete(scopeId)
    }
  }, [])

  const setScopeRegion = useCallback((scopeId: ScopeId, el: HTMLElement): (() => void) => {
    regionsRef.current.set(scopeId, el)
    return () => { if (regionsRef.current.get(scopeId) === el) regionsRef.current.delete(scopeId) }
  }, [])

  const getMenuCommands = useCallback((group: MenuGroup): Command[] => {
    const chain = getScopeChain(resolveActive(), scopesRef.current, coversRef.current)
    return resolveMenuCommands(chain, group)
  }, [])

  const handleShortcut = useCallback((e: KeyboardEvent): boolean =>
    dispatchShortcut(e, getScopeChain(resolveActive(), scopesRef.current, coversRef.current)), [])
  /** Run the first enabled, uncovered command in `chain` (innermost first) matching `e`. */
  const dispatchShortcut = (e: KeyboardEvent, chain: Array<{ scope: CommandScope; covered: boolean }>): boolean => {
    const seen = new Set<string>()
    for (const { scope, covered } of chain) {
      for (const cmd of scope.commands) {
        if (seen.has(cmd.id)) continue
        seen.add(cmd.id)
        if (cmd.shortcut && cmd.enabled && !covered && matchesShortcut(cmd.shortcut, e)) {
          cmd.execute()
          return true
        }
      }
    }
    return false
  }

  // Stable context value — never changes reference.
  // Menu reads fresh commands from refs when it opens (no re-render needed).
  const value = useRef<CommandRegistryValue>({
    registerScope,
    setActiveScope,
    get activeScopeId() { return activeScopeRef.current },
    getMenuCommands,
    handleShortcut,
    coverScope,
    setScopeRegion,
    get dispatchesKeys() { return keyboardRef.current },
  }).current

  // #900 — the keyboard path (see the file header). Registered ONCE, for the provider's life, and `keyboard` read at
  // keystroke time: re-registering on every show/hide moved this listener behind the editors' own `window` listeners,
  // so which of two handlers ran first depended on how often the user had switched workspaces.
  const lastPlaceRef = useRef<{ target: EventTarget | null; region: HTMLElement | null; typing: boolean }>({ target: null, region: null, typing: false })
  useEffect(() => {
    // Where the user last WAS: the last thing pressed, or focused by keyboard (Tab into the chat composer must not
    // leave a click in the explorer answering a later Backspace once focus drops back to the page).
    // ONE trail of where the user last was — the last thing pressed or focused, whichever came last — with the region
    // it was in, taken THEN: a clicked cell span that the click's own re-render replaces still leaves the user in the
    // grid. A place whose region has left the page answers nothing (an older click elsewhere never stands in).
    const regionOf = (t: EventTarget | null): { id: ScopeId; el: HTMLElement } | null => {
      if (!(t instanceof Node)) return null
      let best: { id: ScopeId; el: HTMLElement } | null = null
      for (const [id, el] of regionsRef.current) if (el.contains(t) && (!best || best.el.contains(el))) best = { id, el }
      return best
    }
    // …and whether it was a TEXT FIELD: one that has since gone (an inline rename committed with Enter) still owns the
    // keystrokes that follow — they are the user's typing, not the pane's commands. (A gone BUTTON does not: a cell
    // renderer's link replaced by the click's own re-render must not leave the grid's Delete Rows dead.)
    const record = (t: EventTarget | null) => {
      lastPlaceRef.current = { target: t, region: regionOf(t)?.el ?? null, typing: isTypingTarget(t) }
    }
    const onPointerDown = (e: PointerEvent) => record(e.target)
    const onFocusIn = (e: FocusEvent) => {
      if (e.target === document.body) return
      record(e.target)
      // Keyboard users: Tabbing INTO a region makes its scope the active one, as a click there does (useFocusScope).
      const owner = regionOf(e.target)
      if (owner) setActiveScope(owner.id)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (!keyboardRef.current) return
      // A held key repeats its keydown; a command runs once per press (the grid's Delete Rows is a toggle).
      if (e.repeat || e.defaultPrevented || isTypingTarget(e.target) || isAltGrChord(e)) return
      // Where the keystroke comes from: the focused element, or the last place the user was when focus is the page.
      const place = lastPlaceRef.current
      const onPage = !(e.target instanceof HTMLElement) || e.target === document.body
      const from: Node | null = !onPage
        ? e.target as HTMLElement
        : place.target instanceof Node && place.target.isConnected ? place.target : place.region
      if (!(from instanceof Element) || !from.isConnected) return
      const plainKey = !e.metaKey && !e.ctrlKey && !e.altKey
      // The last place was a text field that has since GONE: a plain key that follows (Backspace) is still the user's
      // typing. Chords (Cmd+C) go on to the pane. A field merely blurred (Escape out of a filter) has no claim.
      if (onPage && plainKey && place.typing && !(place.target instanceof Node && place.target.isConnected)) return
      // A button / link answers its own plain keys (Backspace, F2…) — a toolbar button inside a grid is not "the
      // rows". Checked on `from`: Safari and Firefox don't focus a clicked button, so focus stays on the page.
      if (plainKey && from.closest(KEY_OWNING_CONTROLS)) return
      const chain = getScopeChain(resolveActive(), scopesRef.current, coversRef.current)
      const at = chain.findIndex(({ scope }) => regionsRef.current.has(scope.scopeId))
      const region = at >= 0 ? regionsRef.current.get(chain[at]!.scope.scopeId) : undefined
      if (!region || !region.contains(from) || !region.getClientRects().length) return
      // Cmd+C / Cmd+X over highlighted text IN THIS PANE is the browser's copy (the explorer's own handler makes the
      // same exception); a stale highlight elsewhere doesn't take the pane's copy away.
      const k = e.key.toLowerCase()
      if ((e.metaKey || e.ctrlKey) && (k === 'c' || k === 'x')) {
        const sel = window.getSelection()
        // Started or ended in the pane: a page-wide Cmd+A (anchored at the document's start) doesn't take the pane's
        // copy away, a highlight dragged into the pane does. (One spanning the whole pane from outside reads as
        // page-wide too — the two can't be told apart.)
        const inPane = (n: Node | null) => !!n && region.contains(n)
        if (sel && !sel.isCollapsed && sel.toString() && (inPane(sel.anchorNode) || inPane(sel.focusNode))) return
      }
      // A DISPLAYED modal or popup menu is up: its keys are its own, whatever still has focus behind it — the grid's
      // right-click menu leaves focus on the cell, and Backspace there must not mark the rows behind the open menu.
      // (Last: a full-DOM query; one left open in a hidden keep-alive deck doesn't count.)
      for (const m of document.querySelectorAll('[aria-modal="true"], [role="menu"]')) if (m.getClientRects().length) return
      // Only the scopes AT or BELOW the region's owner: an ancestor with no region of its own (the workspace's Save
      // All) is not this pane's to answer from the keyboard.
      if (dispatchShortcut(e, chain.slice(0, at + 1))) e.preventDefault()
    }
    // Capture for the pointer and focus (a handler that stops propagation can't hide them); bubble on WINDOW for the
    // key — after every element and `document` listener, so their `preventDefault` is seen.
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('focusin', onFocusIn, true)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('focusin', onFocusIn, true)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CommandRegistryContext.Provider value={value}>
      {children}
    </CommandRegistryContext.Provider>
  )
}

// ─── Hooks ──────────────────────────────────────────────────────────────

/**
 * Register a set of commands under a scope.
 * On mount: registers the scope (triggers version bump for new scopes).
 * On update: silently replaces commands in the ref (no re-render — menu reads fresh on open).
 * On unmount: unregisters the scope.
 */
export function useCommandScope(
  scopeId: ScopeId,
  parentScopeId: ScopeId | undefined,
  commands: Command[],
): void {
  const registry = useCommandRegistryOptional()
  const commandsRef = useRef(commands)
  commandsRef.current = commands

  useEffect(() => {
    if (!registry) return
    // Register with a proxy that always reads the latest commands from the ref
    const scope: CommandScope = {
      scopeId,
      parentScopeId,
      get commands() { return commandsRef.current },
    }
    return registry.registerScope(scope)
    // Only re-run on scopeId/parentScopeId change (mount/unmount), NOT on commands change
  }, [scopeId, parentScopeId, registry]) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Track focus for a container element. When the user clicks inside,
 * the scope becomes active. Attaches a mousedown capture listener.
 */
export function useFocusScope(
  scopeId: ScopeId,
  containerRef: React.RefObject<HTMLElement | null>,
): void {
  const registry = useCommandRegistryOptional()
  useEffect(() => {
    const container = containerRef.current
    if (!container || !registry) return
    const onMouseDown = () => registry.setActiveScope(scopeId)
    container.addEventListener('mousedown', onMouseDown, true)
    // …and the container is the scope's keyboard REGION (#900, see the file header).
    const forget = registry.setScopeRegion(scopeId, container)
    return () => {
      container.removeEventListener('mousedown', onMouseDown, true)
      forget()
    }
  }, [scopeId, registry, containerRef])
}

/**
 * Keep `scopeId` COVERED (see the file header) while `covered` is true. For a component that is itself inside the
 * provider; {@link CoverCommandScope} is the same as an element, for a parent that renders the provider.
 */
export function useCoverCommandScope(scopeId: ScopeId, covered: boolean): void {
  const registry = useCommandRegistryOptional()
  useEffect(() => {
    if (!covered || !registry) return
    return registry.coverScope(scopeId)
  }, [covered, scopeId, registry])
}

/** {@link useCoverCommandScope} as an element — render it INSIDE the `CommandRegistryProvider`. */
export function CoverCommandScope({ scopeId, covered }: { scopeId: ScopeId; covered: boolean }): null {
  useCoverCommandScope(scopeId, covered)
  return null
}
